package com.partejar.app.core.ui.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.animation.core.MutableTransitionState
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.partejar.app.core.format.parseBrazilianDate
import com.partejar.app.core.format.parseClockTime
import com.partejar.app.core.format.formatBrazilianDate
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import kotlinx.datetime.LocalDateTime

/** Seletor comum que mantém o contrato REST em ISO e apresenta os rascunhos em pt-BR. */
@Composable
fun PartejarDateTimePicker(
    onDismiss: () -> Unit,
    initialValue: String? = null,
    onSelected: (String) -> Unit
) {
    val initial = remember(initialValue) { initialValue?.let { runCatching { LocalDateTime.parse(it) }.getOrNull() } }
    var date by remember(initialValue) { mutableStateOf(initial?.date?.toString()?.let(::formatBrazilianDate).orEmpty()) }
    var time by remember(initialValue) { mutableStateOf(initial?.time?.toString()?.take(5).orEmpty()) }
    var confirmedValue by remember { mutableStateOf<String?>(null) }
    val visibility = remember { MutableTransitionState(false).apply { targetState = true } }
    val parsedDate = parseBrazilianDate(date)
    val parsedTime = parseClockTime(time)
    val spacing = PartejarTheme.spacing
    LaunchedEffect(visibility.isIdle, visibility.currentState) {
        if (visibility.isIdle && !visibility.currentState && !visibility.targetState) {
            confirmedValue?.let(onSelected) ?: onDismiss()
        }
    }
    val dismiss = { visibility.targetState = false }
    Dialog(
        onDismissRequest = dismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        PartejarVisibility(visibility) {
            Surface(
                modifier = Modifier
                    .windowInsetsPadding(WindowInsets.safeDrawing)
                    .padding(horizontal = spacing.space16)
                    .fillMaxWidth(),
                shape = MaterialTheme.shapes.extraLarge,
                color = MaterialTheme.colorScheme.surfaceContainerLow
            ) {
                Column(
                    modifier = Modifier
                        .verticalScroll(rememberScrollState())
                        .padding(spacing.space16),
                    verticalArrangement = Arrangement.spacedBy(spacing.space16)
                ) {
                    Column(verticalArrangement = Arrangement.spacedBy(spacing.space4)) {
                        Text(
                            "Selecionar data e hora",
                            style = MaterialTheme.typography.titleLarge,
                            modifier = Modifier.semantics { heading() }
                        )
                        Text(
                            "Informe quando o próximo contato acontecerá.",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                        Text("Data", style = MaterialTheme.typography.titleSmall)
                        PartejarDateField(
                            value = date,
                            onValueChange = { date = it },
                            label = "Data",
                            error = date.takeIf { it.length == 10 && parsedDate == null }
                                ?.let { "Informe uma data válida." },
                            preferCompactPicker = true
                        )
                    }
                    Column(verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                        Text("Hora", style = MaterialTheme.typography.titleSmall)
                        PartejarTimeField(
                            value = time,
                            onValueChange = { time = it },
                            label = "Hora"
                        )
                    }
                    PartejarButton(
                        text = "Confirmar",
                        onClick = {
                            confirmedValue = LocalDateTime(requireNotNull(parsedDate), requireNotNull(parsedTime)).toString()
                            dismiss()
                        },
                        enabled = parsedDate != null && parsedTime != null && visibility.targetState,
                        leadingIcon = { Icon(PartejarIcons.Feedback.success, contentDescription = null) },
                        motionEnabled = true
                    )
                    PartejarButton(
                        text = "Cancelar",
                        onClick = dismiss,
                        enabled = visibility.targetState,
                        variant = PartejarButtonVariant.TERTIARY,
                        leadingIcon = { Icon(PartejarIcons.Actions.back, contentDescription = null) },
                        motionEnabled = true
                    )
                }
            }
        }
    }
}
